import { useEffect, useState } from "react";
import { ExternalLink, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

function Metric({ label, value, suffix = "" }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="font-display text-2xl">{value ?? "—"}{value != null ? suffix : ""}</div>
      <div className="mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

export function SeoTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/seo/overview").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) return <div className="h-40 animate-pulse rounded-xl bg-secondary/40" data-testid="seo-tab-loading" />;
  if (!data) return <div className="text-sm text-muted-foreground">Could not load SEO overview.</div>;
  const w = data.website;

  return (
    <div className="space-y-8" data-testid="seo-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg">SEO & Growth — {data.month}</h3>
        <button onClick={load} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground" data-testid="seo-refresh-btn">
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div>
        <div className="mb-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">Website (this month)</div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="seo-website-metrics">
          <Metric label="Unique visitors" value={w.visitors_month} />
          <Metric label="Signups" value={w.signups_month} />
          <Metric label="Signup conversion" value={w.signup_conversion_pct} suffix="%" />
          <Metric label="Revenue" value={`£${w.revenue_month}`} />
          <Metric label="Total users" value={w.total_users} />
          <Metric label="Paying users" value={w.paying_users} />
          <Metric label="Revenue conversion" value={w.revenue_conversion_pct} suffix="%" />
          <Metric label="30-day retention" value={w.retention_30d_pct} suffix="%" />
        </div>
        {w.visitors_month === 0 && (
          <p className="mt-2 text-xs text-muted-foreground">Visitor tracking just went live — numbers build from today.</p>
        )}
      </div>

      <div>
        <div className="mb-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">Technical SEO health (live checks on zuntria.co.uk)</div>
        <div className="space-y-2" data-testid="seo-health-checks">
          {data.health_checks.map((c) => (
            <div key={c.check} className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-sm">
              <span className="flex items-center gap-2">
                {c.status === "pass"
                  ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  : <XCircle className="h-4 w-4 text-red-500" />}
                {c.check}
              </span>
              <span className="text-xs text-muted-foreground">{c.detail}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">Google & search engines</div>
        <p className="mb-3 text-xs text-muted-foreground">{data.google.note}</p>
        <div className="flex flex-wrap gap-2" data-testid="seo-google-links">
          {[["Search Console", data.google.search_console], ["Bing Webmaster", data.google.bing_webmaster],
            ["PageSpeed", data.google.pagespeed], ["Rich Results Test", data.google.rich_results_test]].map(([label, url]) => (
            <a key={label} href={url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:border-primary/60">
              {label} <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
